import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, Crown, Target, Users, Award, ArrowRight, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/zkwjbg2h_Royal%20Delights%20Logo.png";
const CHEF_JAYLON = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/n5u32e78_20200430_145615%20%281%29.jpg";
const CHEF_HANDS = "https://images.unsplash.com/photo-1661505300261-d45fa3e29890?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjaGVmJTIwaGFuZHMlMjBwbGF0aW5nJTIwZm9vZCUyMGRhcmslMjBtb29keXxlbnwwfHx8fDE3NzA4NjU0NDR8MA&ixlib=rb-4.1.0&q=85";
const LUXURY_TABLE = "https://images.unsplash.com/photo-1674818534586-78ababb77227?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBwcml2YXRlJTIwZGluaW5nJTIwYmxhY2slMjBnb2xkJTIwdGFibGUlMjBzZXR0aW5nJTIwY2FuZGxlbGlnaHR8ZW58MHx8fHwxNzcwODY1NDM2fDA&ixlib=rb-4.1.0&q=85";
const GOLDEN_CROWN = "https://images.unsplash.com/photo-1695503117452-22dfcc72e6a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBnb2xkJTIwY3Jvd24lMjByb3lhbCUyMGFlc3RoZXRpYyUyMGRhcmt8ZW58MHx8fHwxNzcwODY1NDQ4fDA&ixlib=rb-4.1.0&q=85";
const DINNER_PARTY = "https://images.unsplash.com/photo-1768508950434-fc76c9f3ed9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwZGlubmVyJTIwcGFydHklMjBndWVzdHMlMjBsYXVnaGluZyUyMGNoYW1wYWduZSUyMGRhcmslMjBhbWJpZW50fGVufDB8fHx8MTc3MDg2NTQ2NXww&ixlib=rb-4.1.0&q=85";

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 1 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8 } }
};

export const AboutPage = () => {
  const philosophyPoints = [
    { 
      icon: <Heart className="w-6 h-6" />, 
      title: "Soulful Flavor", 
      description: "Every dish carries the depth of tradition and the warmth of home cooking, elevated to luxury." 
    },
    { 
      icon: <Sparkles className="w-6 h-6" />, 
      title: "Luxury Presentation", 
      description: "We believe presentation is the first taste. Every plate is a work of art." 
    },
    { 
      icon: <Users className="w-6 h-6" />, 
      title: "Over-Prepared", 
      description: "We arrive with more than enough — staff, supplies, and spirit — so nothing is left to chance." 
    },
    { 
      icon: <Target className="w-6 h-6" />, 
      title: "Seamless Execution", 
      description: "From the first flame to the final cleanup, every moment flows effortlessly." 
    },
    { 
      icon: <Crown className="w-6 h-6" />, 
      title: "Genuine Connection", 
      description: "We don't just serve food. We create moments that bring people together." 
    }
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-[#050505]">
      
      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img 
            src={CHEF_HANDS} 
            alt="Chef crafting" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-24 relative z-10 w-full">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl"
          >
            <motion.p variants={fadeInUp} className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4 font-body">
              The Story
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-[#F5F5F0] mb-6">
              Meet Chef <span className="italic text-[#D4AF37]">Jaylon</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#A1A1AA] leading-relaxed">
              A visionary chef-owner whose craft blends soulful seasoning with refined technique, 
              rooted in culture, family, and the art of making people feel celebrated.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ==================== CHEF STORY ==================== */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative order-2 lg:order-1"
            >
              <div className="relative">
                {/* Main Image - Chef Jaylon */}
                <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                  <img 
                    src={CHEF_JAYLON} 
                    alt="Chef Jaylon at a Royal Delights event" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                
                {/* Decorative Elements */}
                <div className="absolute -bottom-8 -right-8 w-48 h-48 border border-[#D4AF37]/30 rounded-2xl" />
                
                {/* Stats Card */}
                <div className="absolute -bottom-6 left-6 bg-[#121212] border border-white/10 rounded-xl p-6 shadow-2xl">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <Award className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="font-heading text-2xl font-bold text-[#F5F5F0]">10+</p>
                      <p className="font-body text-sm text-[#A1A1AA]">Years of Excellence</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="order-1 lg:order-2"
            >
              <motion.p variants={fadeInUp} className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4 font-body">
                The Journey
              </motion.p>
              <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F5F5F0] mb-8">
                From Passion to <span className="italic">Excellence</span>
              </motion.h2>
              
              <motion.div variants={staggerContainer} className="space-y-6 font-body text-[#A1A1AA] text-lg leading-relaxed">
                <motion.p variants={fadeInUp}>
                  Royal Delights was born from a simple belief: <span className="text-[#F5F5F0]">food should make people feel seen, 
                  celebrated, and cared for</span>. What began as cooking for family gatherings evolved into 
                  a mission to bring that same warmth to every table we touch.
                </motion.p>
                <motion.p variants={fadeInUp}>
                  From intimate dinners to large celebrations, Chef Jaylon brings <span className="text-[#D4AF37]">precision, 
                  warmth, and luxury</span> to every event — transforming hibachi into a soulful, elevated 
                  experience that honors both tradition and innovation.
                </motion.p>
                <motion.p variants={fadeInUp}>
                  Every flame, every sizzle, every carefully placed garnish tells a story of dedication 
                  to the craft and <span className="text-[#F5F5F0]">deep respect for the guests we serve</span>.
                </motion.p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== QUOTE SECTION ==================== */}
      <section className="py-24 bg-[#121212] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={GOLDEN_CROWN} alt="" className="w-full h-full object-contain object-center" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn}>
              <Quote className="w-12 h-12 text-[#D4AF37] mx-auto mb-8" />
            </motion.div>
            <motion.p 
              variants={fadeInUp}
              className="font-accent text-2xl sm:text-3xl lg:text-4xl text-[#F5F5F0] italic leading-relaxed"
            >
              "Cooking is my love language. When I'm at the grill, I'm not just preparing food — 
              I'm crafting memories, honoring tradition, and showing every guest that they deserve 
              to be treated like royalty."
            </motion.p>
            <motion.p variants={fadeInUp} className="mt-8 text-[#D4AF37] font-body text-sm uppercase tracking-widest">
              — Chef Jaylon
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ==================== PHILOSOPHY ==================== */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-16 max-w-3xl mx-auto">
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4 font-body">Our Values</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#F5F5F0] mb-6">
                The Royal Philosophy
              </h2>
              <p className="font-body text-lg text-[#A1A1AA]">
                At Royal Delights, hospitality is more than service — it's culture. Every decision 
                we make is guided by these core principles.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {philosophyPoints.map((point, index) => (
                <motion.div
                  key={index}
                  variants={scaleIn}
                  className="bg-[#121212] border border-white/5 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-colors duration-500 group text-center"
                  data-testid={`philosophy-card-${index}`}
                >
                  <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-5 group-hover:bg-[#D4AF37]/20 transition-colors">
                    <span className="text-[#D4AF37]">{point.icon}</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-[#F5F5F0] mb-3">{point.title}</h3>
                  <p className="font-body text-sm text-[#A1A1AA] leading-relaxed">{point.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== LEGACY VISION ==================== */}
      <section className="py-24 lg:py-32 bg-[#121212] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={DINNER_PARTY} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#121212] via-[#121212]/90 to-[#121212]/70" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 mb-4">
                <Crown className="w-5 h-5 text-[#D4AF37]" />
                <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm font-body">Vision</p>
              </motion.div>
              
              <motion.h2 variants={fadeInUp} className="font-heading text-4xl sm:text-5xl font-bold text-[#F5F5F0] mb-6">
                Building a <span className="italic text-[#D4AF37]">Legacy</span>
              </motion.h2>
              
              <motion.p variants={fadeInUp} className="font-body text-lg text-[#A1A1AA] leading-relaxed mb-8">
                Royal Delights is part of a larger mission — building a legacy through food, culture, 
                and storytelling. With soulful luxury at its core, the brand is evolving into a 
                hospitality experience worthy of national recognition.
              </motion.p>
              
              <motion.p variants={fadeInUp} className="font-body text-lg text-[#A1A1AA] leading-relaxed mb-10">
                Every event we cater, every family we serve, and every celebration we enhance brings us 
                closer to that vision — <span className="text-[#F5F5F0]">a world where everyone experiences the warmth of being treated like royalty</span>.
              </motion.p>
              
              <motion.div variants={fadeInUp}>
                <Link to="/experience">
                  <Button 
                    data-testid="learn-experience-btn"
                    className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-semibold rounded-full px-8 py-4 transition-all duration-300 hover:scale-105"
                  >
                    Explore The Experience
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            {/* Visual Element */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="aspect-square rounded-full border-2 border-[#D4AF37]/20 flex items-center justify-center p-12">
                  <div className="aspect-square rounded-full border border-[#D4AF37]/40 flex items-center justify-center p-12 w-full">
                    <img src={LOGO_URL} alt="Royal Delights" className="w-32 h-32 object-contain" />
                  </div>
                </div>
                
                {/* Floating badges */}
                <div className="absolute top-0 right-0 bg-[#050505] border border-white/10 rounded-xl px-4 py-2">
                  <p className="font-body text-sm text-[#D4AF37]">Atlanta's Finest</p>
                </div>
                <div className="absolute bottom-0 left-0 bg-[#050505] border border-white/10 rounded-xl px-4 py-2">
                  <p className="font-body text-sm text-[#D4AF37]">Since 2020</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <motion.div 
          className="max-w-4xl mx-auto px-6 lg:px-8 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl font-bold text-[#F5F5F0] mb-6"
          >
            Ready for the <span className="italic text-[#D4AF37]">Royal Treatment</span>?
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-[#A1A1AA] mb-10 max-w-2xl mx-auto"
          >
            Let Chef Jaylon and the Royal Delights team create an unforgettable experience for your next celebration.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/booking">
              <Button 
                data-testid="about-cta-btn"
                className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-semibold rounded-full px-10 py-6 text-base transition-all duration-300 hover:scale-105 shadow-[0_0_30px_-10px_rgba(212,175,55,0.5)]"
              >
                Book Your Experience
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default AboutPage;
